import type { ApolloCache } from '@apollo/client'
import {
  catchError,
  concat,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  ignoreElements,
  map,
  merge,
  mergeScan,
  Observable,
  of,
  shareReplay,
  skipWhile,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs'

import { isNotNull } from '../../utils/collectionTools'
import { CustomError, logAndCaptureException } from '../../utils/errorTools'
import { CartMirrorState, cartState$ as defaultCartState$ } from '../cart/state/cart'
import { isCartSettled } from '../cart/state/cart-mutation'
import { latestApolloClient$ } from '../graphql/apollo'
import {
  CrossTabChannel,
  crossTabChannel,
  CrossTabMessage,
  latestVisibleByTag,
  VisibilityGate,
} from './channel'
import { cartFingerprint } from './fingerprint'

export type CartMessage = Extract<CrossTabMessage, { readonly _tag: 'cart' }>

export type CartTriple = Pick<CartMessage, 'cartId' | 'kind' | 'fingerprint'>

export type CartReaction =
  | { readonly _tag: 'refetch' }
  | { readonly _tag: 'replace' }
  | { readonly _tag: 'none' }

export interface CartCacheClient {
  readonly cache: Pick<ApolloCache<unknown>, 'evict' | 'gc'>
}

export interface CartBreakerConfig {
  readonly limit: number
  readonly windowMs: number
  readonly cooldownMs: number
}

export interface CartBreakerReaction {
  readonly at: number
  readonly cartId: string
  readonly fingerprint: string
}

export interface CartBreakerState {
  readonly executed: readonly (CartBreakerReaction & { readonly repeat: boolean })[]
  readonly openUntil: number | null
}

export type CartBreakerVerdict =
  | { readonly _tag: 'pass' }
  | { readonly _tag: 'trip'; readonly reactions: number }
  | { readonly _tag: 'open' }

export class CrossTabCartLoopError extends CustomError {
  override name = 'CrossTabCartLoopError'

  constructor(
    public readonly cartId: string,
    public readonly reactions: number,
  ) {
    super(`Cross-tab cart reactions for ${cartId} tripped the breaker after ${reactions} reactions`)
  }
}

const DEFAULT_TIMEOUT_MS = 10_000

const DEFAULT_BREAKER: CartBreakerConfig = { limit: 6, windowMs: 60_000, cooldownMs: 5 * 60_000 }

const closedBreaker: CartBreakerState = { executed: [], openUntil: null }

const isCartMessage = (message: CrossTabMessage): message is CartMessage => message._tag === 'cart'

const sameTriple = (previous: CartTriple, current: CartTriple): boolean =>
  previous.cartId === current.cartId &&
  previous.kind === current.kind &&
  previous.fingerprint === current.fingerprint

// A cart in error never starts a refetch, so reacting to one would only wait out the timeout.
const toTriple = ({ cart, type, error }: CartMirrorState): CartTriple | null =>
  cart && type !== 'none' && !error
    ? { cartId: cart.id, kind: type, fingerprint: cartFingerprint(cart) }
    : null

/** This tab's settled triple, `null` while it has no cart to compare against or is in error. */
export const deriveOwnCartTriple = (
  cartState$: Observable<CartMirrorState>,
): Observable<CartTriple | null> =>
  cartState$.pipe(
    filter(isCartSettled),
    map(toTriple),
    distinctUntilChanged(
      (previous, current) =>
        previous === current ||
        (previous !== null && current !== null && sameTriple(previous, current)),
    ),
  )

export const cartReaction = (message: CartTriple, own: CartTriple | null): CartReaction => {
  // A kind mismatch is the auth consumer's to resolve: it is already moving this tab to the
  // other kind.
  if (own === null || message.kind !== own.kind) {
    return { _tag: 'none' }
  }
  if (message.cartId === own.cartId) {
    return message.fingerprint === own.fingerprint ? { _tag: 'none' } : { _tag: 'refetch' }
  }
  // A new guest id already reaches this tab through the `guest_cart_id` storage event; reacting
  // here as well would bring back the thrash from story 124819.
  return message.kind === 'customer' ? { _tag: 'replace' } : { _tag: 'none' }
}

/**
 * Two visible tabs whose global-promotions machines disagree (one applies the promotion, the other
 * removes it) would otherwise refetch each other's changes without end. That loop alternates
 * between fingerprints already heard, while a shopper's edits produce new ones, so only a reaction
 * to a fingerprint already heard for the cart within the window counts toward the limit.
 */
export const stepCartBreaker = (
  state: CartBreakerState,
  reaction: CartBreakerReaction,
  { limit, windowMs, cooldownMs }: CartBreakerConfig,
): { readonly state: CartBreakerState; readonly verdict: CartBreakerVerdict } => {
  if (state.openUntil !== null && reaction.at < state.openUntil) {
    return { state, verdict: { _tag: 'open' } }
  }
  const recent = state.executed.filter(({ at }) => reaction.at - at < windowMs)
  const repeat = recent.some(
    ({ cartId, fingerprint }) => cartId === reaction.cartId && fingerprint === reaction.fingerprint,
  )
  const repeats = recent.filter((executed) => executed.repeat).length
  return repeat && repeats >= limit
    ? {
        state: { executed: [], openUntil: reaction.at + cooldownMs },
        verdict: { _tag: 'trip', reactions: repeats + 1 },
      }
    : {
        state: { executed: [...recent, { ...reaction, repeat }], openUntil: null },
        verdict: { _tag: 'pass' },
      }
}

export interface CrossTabCartDeps {
  readonly channel: CrossTabChannel
  readonly cartState$: Observable<CartMirrorState>
  readonly apolloClient$: Observable<CartCacheClient>
  readonly visibility?: VisibilityGate
  readonly timeoutMs?: number
  readonly breaker?: CartBreakerConfig
  readonly now?: () => number
}

export const createCrossTabCart = ({
  channel,
  cartState$,
  apolloClient$,
  visibility,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  breaker = DEFAULT_BREAKER,
  now = Date.now,
}: CrossTabCartDeps): Observable<unknown> => {
  const ownTriple$ = deriveOwnCartTriple(cartState$).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  // Keeps the `null` of a cart in error, so the consumer stops reacting rather than replaying the
  // last good triple.
  const currentOwnTriple$ = merge(ownTriple$, of(null)).pipe(take(1))

  const publisher$ = ownTriple$.pipe(
    filter(isNotNull),
    distinctUntilChanged(sameTriple),
    tap((triple) => channel.publish({ _tag: 'cart', ...triple })),
  )

  // `cartState$` replays the current settled state on subscription, so the round trip is only over
  // once a load has started and then settled. The wait subscribes before `refetch` runs so it cannot
  // miss a synchronous `loading: true`, and the timeout keeps a refetch that never starts from
  // holding the queue.
  const refetch$ = cartState$.pipe(
    take(1),
    switchMap(({ refetch }) =>
      merge(
        cartState$.pipe(
          skipWhile(({ loading }) => !loading),
          filter(isCartSettled),
          take(1),
          timeout({ first: timeoutMs }),
          catchError(() => EMPTY),
        ),
        defer(() => {
          refetch()
          return EMPTY
        }),
      ),
    ),
  )

  // The same two steps `order.ts` takes after an order. Evicting the field is enough to have the live
  // query fetch the new cart, so the wait is for a settled cart with another id rather than a
  // `refetch`, which would be a second request.
  const replace$ = cartState$.pipe(
    take(1),
    switchMap(({ cart }) =>
      merge(
        cartState$.pipe(
          filter(isCartSettled),
          filter((state) => state.cart !== undefined && state.cart.id !== cart?.id),
          take(1),
          timeout({ first: timeoutMs }),
          catchError(() => EMPTY),
        ),
        apolloClient$.pipe(
          take(1),
          tap(({ cache }) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'customerCart' })
            cache.gc()
          }),
          ignoreElements(),
        ),
      ),
    ),
  )

  const execute = (reaction: CartReaction): Observable<unknown> => {
    switch (reaction._tag) {
      case 'refetch':
        return refetch$
      case 'replace':
        return replace$
      case 'none':
        return EMPTY
    }
  }

  const consumer$ = channel.incoming$.pipe(
    latestVisibleByTag(visibility),
    map(({ message }) => message),
    filter(isCartMessage),
    // Each message is judged when its turn comes, against the cart the previous round trip left, so
    // a burst costs one refetch and the exchange stops once the triples agree. Concurrency 1 keeps
    // that order while the breaker state is carried from one message to the next.
    mergeScan(
      (breakerState: CartBreakerState, message: CartMessage) =>
        currentOwnTriple$.pipe(
          switchMap((own) => {
            const reaction = cartReaction(message, own)
            if (reaction._tag === 'none' || own === null) {
              return of(breakerState)
            }
            const step = stepCartBreaker(
              breakerState,
              { at: now(), cartId: message.cartId, fingerprint: message.fingerprint },
              breaker,
            )
            if (step.verdict._tag === 'trip') {
              logAndCaptureException(new CrossTabCartLoopError(own.cartId, step.verdict.reactions))
            }
            return concat(
              step.verdict._tag === 'pass' ? execute(reaction).pipe(ignoreElements()) : EMPTY,
              of(step.state),
            )
          }),
        ),
      closedBreaker,
      1,
    ),
  )

  return merge(publisher$, consumer$)
}

export const crossTabCart$: Observable<unknown> = createCrossTabCart({
  channel: crossTabChannel,
  cartState$: defaultCartState$,
  apolloClient$: latestApolloClient$,
})
